"use client";

import {
  type FC,
  type HTMLAttributes,
  type ReactNode,
  useRef,
  useState,
  useEffect,
} from "react";
import { mergeClasses } from "@huub-web/utils";

import type { ChatConversation, ChatMessage } from "../../types";
import { Flex } from "../Flex";
import { UserMessage } from "../UserMessage";
import { AgentMessage } from "./components/AgentMessage";
import { useWindowSize } from "../../hooks";
import { useIsomorphicLayoutEffect } from "../../hooks/useIsomorphicLayoutEffect";

import {
  agentMessageStyle,
  containerStyle,
  messageContainerStyle,
  userMessageStyle,
} from "./ChatView.css";

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type ChatViewProps<EntryData = any> = HTMLAttributes<HTMLDivElement> & {
  conversation?: ChatConversation<EntryData>;
  trailing?: ReactNode;
  variant?: ChatViewVariant;
  debug?: boolean;
  showTime?: boolean;
  showAuthor?: boolean;
  allowSizing?: boolean;
  mapAIResponseTrailing?: (data: ChatMessage<EntryData>) => ReactNode;
};

export type ChatViewVariant = "default" | "compact" | "sideContent";

/**
 * A component that displays a chat history.
 */
export const ChatView: FC<ChatViewProps> = ({
  conversation,
  trailing,
  showTime = false,
  showAuthor = false,
  debug = false,
  variant = "default",
  allowSizing = true,
  mapAIResponseTrailing,
  className,
  ...props
}) => {
  const ref = useRef<HTMLDivElement>(null);
  const windowSize = useWindowSize();

  const [containerHeight, setContainerHeight] = useState<number | null>(null);

  useIsomorphicLayoutEffect(() => {
    if (ref.current) {
      setContainerHeight(ref.current.clientHeight);
    }
  }, [windowSize]);

  useEffect(() => {
    if (containerHeight) {
      ref.current?.style.setProperty(
        "--container-height",
        `${containerHeight}px`,
      );
    }
  }, [containerHeight]);

  return (
    <Flex
      ref={ref}
      direction="column"
      className={mergeClasses(containerStyle({ variant }), className)}
      grow
      {...props}
    >
      {conversation?.entries.map((entry) =>
        entry.author === "agent" ? (
          <Flex
            key={entry.id}
            align="flex-start"
            className={messageContainerStyle({
              apply: entry.interaction === true && allowSizing,
            })}
          >
            <AgentMessage
              className={agentMessageStyle({ variant })}
              showTime={showTime}
              message={entry}
              variant={variant}
              debug={debug}
            >
              {mapAIResponseTrailing?.(entry)}
            </AgentMessage>
          </Flex>
        ) : (
          <Flex
            key={entry.id}
            justify="flex-end"
            className={messageContainerStyle({
              apply: true && allowSizing,
            })}
          >
            <UserMessage
              message={entry}
              className={userMessageStyle({ variant })}
              variant={variant}
              showTime={showTime}
              showAuthor={showAuthor}
              debug={debug}
            />
          </Flex>
        ),
      )}
      {trailing}
    </Flex>
  );
};
